import { useEffect, useRef, useState } from "react";
import { Lightbulb, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";

const ExplainEs = ({ text, idx }) => {
  const [t, setT] = useState("");
  useEffect(() => { if (text) http.post("/translate", { text: text.slice(0, 2000) }).then((r) => setT(r.data.es)).catch(() => {}); }, [text]);
  return t ? <p className="mt-1 text-sm text-[#9A4F1C] italic" data-no-translate data-testid={`quiz-q${idx}-explanation-es`}>{t}</p> : null;
};

const Question = ({ q, idx, result, onPick, es }) => {
  const [hint, setHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const answered = result !== undefined;
  const picked = result?.choice;
  const pick = async (i) => { setBusy(true); await onPick(i); setBusy(false); };
  return (
    <div className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6" data-testid={`quiz-question-${idx}`}>
      <p className="font-display text-lg text-[#2C4A3E]">{idx + 1}. {q.question}</p>
      {es && <p className="text-sm text-[#9A4F1C] italic mt-1" data-no-translate data-testid={`quiz-q${idx}-es`}>{es.q}</p>}
      <div className="grid sm:grid-cols-2 gap-2 mt-4">
        {q.options.map((o, i) => {
          const correct = answered && i === result.answer_index;
          const cls = !answered ? "bg-white/70 hover:border-[#2C4A3E]" : correct ? "bg-[#E3EBE5] border-[#6B9080]" : picked === i ? "bg-[#F6E1DC] border-[#C87D53]" : "bg-white/40 opacity-60";
          return (
            <button key={i} disabled={answered || busy} onClick={() => pick(i)} data-testid={`quiz-q${idx}-option-${i}`}
              className={`text-left px-4 py-3 rounded-2xl border border-[#E6DEC3] transition-colors flex items-center gap-2 ${cls}`}>
              {answered && correct && <Check className="w-4 h-4 text-[#2C4A3E] shrink-0" />}
              {answered && picked === i && !correct && <X className="w-4 h-4 text-[#C87D53] shrink-0" />}
              <span>{o}{es?.o[i] && <span className="block text-xs text-[#9A4F1C] italic" data-testid={`quiz-q${idx}-option-${i}-es`}>{es.o[i]}</span>}</span>
            </button>
          );
        })}
      </div>
      {!answered && (
        <button onClick={() => setHint(!hint)} data-testid={`quiz-q${idx}-hint-btn`} className="mt-3 text-sm text-[#C87D53] inline-flex items-center gap-1">
          <Lightbulb className="w-4 h-4" /> Mindful pause
        </button>
      )}
      {hint && !answered && <p className="mt-2 text-sm text-stone-600 italic">{q.mindful_hint}</p>}
      {answered && <p className="mt-3 text-sm text-stone-600" data-testid={`quiz-q${idx}-explanation`}>{result.explanation}</p>}
      {answered && es && <ExplainEs text={result.explanation} idx={idx} />}
    </div>
  );
};

export const QuizStep = ({ req, lesson, onDone }) => {
  const [qs, setQs] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [selfPrompt, setSelfPrompt] = useState("");
  const started = useRef(false);
  const { profile } = useProfile();
  const [es, setEs] = useState(null);
  const [esTry, setEsTry] = useState(0);
  const bilingual = profile?.role === "student" && !!profile.spanish_help;
  useEffect(() => {
    if (!bilingual || !qs) return setEs(null);
    const texts = qs.flatMap((q) => [q.question, ...q.options]);
    http.post("/translate/batch", { texts }).then((r) => {
      let k = 0;
      setEs(qs.map((q) => ({ q: r.data.es[k++], o: q.options.map(() => r.data.es[k++]) })));
    }).catch(() => setEs("failed"));
  }, [bilingual, qs, esTry]);

  const load = () => {
    setError(""); setQs(null); setAnswers({});
    http.post("/quiz", { ...req, lesson_text: lesson }).then((r) => { setQs(r.data.questions); setQuizId(r.data.quiz_id); setSelfPrompt(r.data.self_reflection_prompt || ""); })
      .catch(() => setError("Couldn't create the practice questions. Please try again."));
  };
  useEffect(() => { if (!started.current) { started.current = true; load(); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const done = qs && Object.keys(answers).length === qs.length;
  const score = Object.values(answers).filter((a) => a.correct).length;
  const finish = () => { toast.success(`You got ${score} of ${qs.length}. Every answer is a step in growth.`); onDone(quizId, selfPrompt); };
  const answer = async (i, choice) => {
    try {
      const r = await http.post(`/quiz/${quizId}/answer`, { index: i, choice });
      setAnswers((a) => ({ ...a, [i]: r.data }));
    } catch { toast.error("Couldn't check that answer. Please try again."); }
  };

  if (error) return <div data-testid="quiz-error"><p className="text-red-700 bg-red-50 rounded-xl p-4">{error}</p>
    <button onClick={load} data-testid="quiz-retry-btn" className="mt-4 px-5 py-2 rounded-full border border-[#E6DEC3]">Retry</button></div>;
  if (!qs) return <div className="py-16 text-center" data-testid="quiz-loading">
    <div className="w-14 h-14 mx-auto rounded-full bg-[#D99B26]/40 animate-pulse" /><p className="mt-4 font-display text-lg text-[#2C4A3E]">Preparing gentle practice…</p></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="quiz-step">
      {bilingual && (es === "failed"
        ? <p className="text-xs text-[#C87D53]" data-testid="quiz-bilingual-failed">Spanish isn't ready. <button onClick={() => { setEs(null); setEsTry((n) => n + 1); }} data-testid="quiz-bilingual-retry" className="underline">Try again</button></p>
        : <p className="text-xs uppercase tracking-widest text-[#C87D53] font-semibold" data-testid="quiz-bilingual-label">{es ? "English + Español" : "Preparing Spanish…"}</p>)}
      {qs.map((q, i) => <Question key={i} q={q} idx={i} result={answers[i]} es={Array.isArray(es) ? es[i] : null} onPick={(v) => answer(i, v)} />)}
      {done && (
        <div className="flex items-center gap-4 pt-4">
          <p className="font-display text-xl text-[#2C4A3E]" data-testid="quiz-score">{score} / {qs.length} correct</p>
          <button onClick={finish} data-testid="quiz-continue-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">Continue to reflection</button>
        </div>
      )}
    </div>
  );
};
